import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Alert,
  Button,
  Chip,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Business as BusinessIcon,
  Theaters as TheaterIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { Breadcrumbs } from "@/utils/components/Breadcrumbs";
import { useStagesApi } from "@/context/AppContext";
import type { Stage } from "api-client/models";

export const StageDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const stagesApi = useStagesApi();
  const [stage, setStage] = useState<Stage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStage();
  }, [id]);

  const fetchStage = async () => {
    try {
      setLoading(true);
      const data = await stagesApi.stagesRetrieve({ id: id! });
      setStage(data);
      setError(null);
    } catch {
      setError("Failed to load stage. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading stage...</Typography>
      </Stack>
    );
  }

  if (error || !stage) {
    return (
      <Box>
        <Alert severity="error">{error || "Stage not found"}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/stages")}>
          Back to Stages
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <Breadcrumbs
        items={[{ label: "Stages", path: "/stages" }, { label: stage.name }]}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h4" gutterBottom>
            {stage.name}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip label={stage.stageType} color="primary" size="small" />
            <Chip
              label={`Capacity: ${stage.capacity || 0}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={
                stage.subscriptionStatus === "active" ? "Active" : "Inactive"
              }
              color={
                stage.subscriptionStatus === "active" ? "success" : "default"
              }
              size="small"
            />
          </Stack>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
          onClick={() => navigate(`/stages/${id}/edit`)}
        >
          Edit Stage
        </Button>
      </Stack>

      <Stack spacing={3}>
        {/* Basic Information */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <BusinessIcon fontSize="small" color="action" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    Venue
                  </Typography>
                  <Typography variant="body1">{stage.venueName}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <TheaterIcon fontSize="small" color="action" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    Capacity
                  </Typography>
                  <Typography variant="body1">{stage.capacity || 0}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CategoryIcon fontSize="small" color="action" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    Stage Type
                  </Typography>
                  <Typography variant="body1">{stage.stageType}</Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Technical Specifications */}
        {stage.technicalSpecs &&
          Object.values(stage.technicalSpecs).some(Boolean) && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Technical Specifications
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  {stage.technicalSpecs.sound_system && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Sound System
                      </Typography>
                      <Typography variant="body1">
                        {stage.technicalSpecs.sound_system}
                      </Typography>
                    </Box>
                  )}
                  {stage.technicalSpecs.lighting && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Lighting
                      </Typography>
                      <Typography variant="body1">
                        {stage.technicalSpecs.lighting}
                      </Typography>
                    </Box>
                  )}
                  {stage.technicalSpecs.stage_dimensions && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Stage Dimensions
                      </Typography>
                      <Typography variant="body1">
                        {stage.technicalSpecs.stage_dimensions}
                      </Typography>
                    </Box>
                  )}
                  {stage.technicalSpecs.load_in_notes && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Load-In Notes
                      </Typography>
                      <Typography variant="body1">
                        {stage.technicalSpecs.load_in_notes}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" mt={3}>
        <Button variant="outlined" onClick={() => navigate("/stages")}>
          Back to Stages
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate(`/venues/${stage.venue}`)}
        >
          View Venue
        </Button>
      </Stack>
    </Box>
  );
};
